import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import VerticalDivider from '@/global/components/Divider/VerticalDivider'
import Skeleton from '@/global/components/Skeleton'
import ContentGuide from '@/global/guides/ContentGuide'
import {
    formatarHoras,
    formatPorcentagemRegra,
    hoursToMinutes as min,
    onlyDefined,
} from '@acme/common'
import dayjs from 'dayjs'
import IconeAjuda from 'global/components/IconeAjuda'
import SaldoBox from 'global/components/SaldoBox'
import { AlertTriangleIcon, TriangleAlertIcon } from 'lucide-react'
import { usePagamentoHorasOutletContext } from 'pages/PagamentoHoras.context'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import {
    funcionario as funcionario_api,
    regrasCalculo,
    SaldoHoras,
} from 'service/api'

interface IProps {
    limite: Partial<SaldoHoras>
}

interface TipoHora {
    tipo: string
    quantidade: string
}

const FALTAS_KEY = 'Faltas'

export default function FormPagamento({ limite }: IProps) {
    const { setValue, watch } = useFormContext()
    const { data: regras } = regrasCalculo.useFetch()
    const { funcionarioId = '-1' } = useParams()
    const { data } = usePagamentoHorasOutletContext()
    const { data: funcionario } = funcionario_api.useFetchById(
        Number(funcionarioId),
    )
    const saldo = data?.find(
        item => item.saldo.funcionarioId === Number(funcionarioId),
    )?.saldo
    const [porcentagens, faltas] = watch(['porcentagens', 'faltas'])

    const [tipos, setTipos] = useState<TipoHora[]>()

    useEffect(() => {
        if (!regras || !funcionario) return
        else {
            const opcoes =
                regras
                    ?.find(r => r.id == funcionario?.regraCalculo)
                    ?.porcentagens.map(p => p?.porcentagem?.toString())
                    .filter(onlyDefined) ?? []

            const defaultValues = Object.entries(porcentagens ?? {})
                .map(([key, value]) => ({
                    tipo: key,
                    quantidade: formatarHoras(value as any, 3),
                }))
                .concat({
                    tipo: FALTAS_KEY,
                    quantidade: formatarHoras(faltas, 3),
                })

            setTipos(
                [
                    ...opcoes
                        .map(v => ({
                            tipo: v,
                            quantidade: '000:00',
                        }))
                        .filter(
                            v => !defaultValues.some(dv => dv.tipo === v.tipo),
                        ),
                    ...defaultValues,
                ].sort((a, b) => a.tipo.localeCompare(b.tipo)),
            )
        }
    }, [regras, funcionario?.id])

    useEffect(() => {
        setValue(
            'valores',
            tipos?.reduce((acc, curr) => {
                return {
                    ...acc,
                    [curr.tipo]: curr.quantidade,
                }
            }, {}),
        )
    }, [tipos])

    if (!limite || !funcionario) return <Loading />

    return [
        <div className="flex flex-row flex-grow gap-4 p-4 mb-4 border rounded-sm items-center justify-evenly">
            <h6
                className="text-xl font-semibold truncate max-w-[65%]"
                title={funcionario?.nome}
            >
                {funcionario?.nome}
            </h6>
            {saldo && saldo.id ? (
                <SaldoBox
                    title={
                        <div className="flex flex-col">
                            <span>Saldo atual</span>
                            <span>
                                {!!saldo.competencia &&
                                    dayjs(saldo.competencia, 'YYYY-MM').format(
                                        'MM/YYYY - MMM',
                                    )}
                            </span>
                        </div>
                    }
                    extras={saldo.horasExtras}
                    faltas={saldo.faltas}
                    porcentagens={saldo.porcentagemHorasExtras as any}
                    tipoFaltas={{
                        Parciais: saldo.faltasParciais,
                        Integrais: saldo.faltasIntegrais,
                        Justificadas: saldo.faltasJustificadas,
                        Injustificadas: saldo.faltasInjustificadas,
                    }}
                    className="border-none"
                />
            ) : (
                <div className="flex flex-row items-center gap-2">
                    <span className="text-sm font-semibold">Saldo atual</span>
                    <VerticalDivider />
                    <div className="text-error font-bold inline-flex items-center gap-1">
                        <AlertTriangleIcon className="size-4" />
                        Não calculado
                    </div>
                </div>
            )}
        </div>,
        <ContentGuide.DataGrid
            loading={false}
            ToolbarProps={{ hideToolbar: true }}
            hideFooter
            columns={[
                {
                    ...optionsColumn,
                    field: 'tipo',
                    headerName: '',
                    width: 200,
                    valueFormatter: ({ value }) =>
                        value === FALTAS_KEY
                            ? 'Faltas'
                            : `Horas extras ${formatPorcentagemRegra(value)}`,
                },
                {
                    ...optionsColumn,
                    field: 'limite',
                    headerName: 'Limite',
                    width: 150,
                    headerAlign: 'center',
                    align: 'left',
                    valueGetter: ({ value }) => formatarHoras(value, 3),
                    renderCell: ({ value, row }) => (
                        <span className="inline-flex items-center justify-between gap-6">
                            {value}{' '}
                            {min(row.quantidade) > min(value) && (
                                <IconeAjuda
                                    size="small"
                                    Icon={
                                        <TriangleAlertIcon className="text-warning" />
                                    }
                                >
                                    O valor que exceder o limite será compensado
                                    no saldo
                                </IconeAjuda>
                            )}
                        </span>
                    ),
                    renderHeader: () => (
                        <span
                            className="inline-flex items-center justify-between gap-6"
                            onClick={e => e.stopPropagation()}
                        >
                            <span>Limite</span>{' '}
                            <IconeAjuda size="small" className="mt-3">
                                (Apenas leitura) Representa a soma do saldo
                                atual com o valor do pagamento. O valor que
                                exceder o limite será compensado no saldo.
                            </IconeAjuda>
                        </span>
                    ),
                },
                {
                    ...optionsColumn,
                    field: 'quantidade',
                    headerName: 'Pagamento',
                    flex: 1,
                    editable: true,
                    headerAlign: 'center',
                    renderHeader: () => (
                        <span
                            className="inline-flex items-center justify-between gap-6"
                            onClick={e => e.stopPropagation()}
                        >
                            <span>Pagamento</span>{' '}
                            <IconeAjuda size="small" className="mt-3">
                                (Editável) Clique sobre os campos para editar.
                            </IconeAjuda>
                        </span>
                    ),
                },
            ]}
            rows={
                tipos?.map((v, i) => ({
                    ...v,
                    limite:
                        v.tipo === FALTAS_KEY
                            ? limite?.faltas
                            : (limite?.porcentagemHorasExtras as any)?.[v.tipo],
                    id: i,
                })) || []
            }
            headerHeight={36}
            rowHeight={35}
            onCellEditCommit={({ id, field, value: rawValue = '' }) => {
                let value = String(rawValue).trim()
                if (field !== 'quantidade') return
                if (!/^\d{1,3}:\d{2}$/.test(value)) {
                    if (value.includes(':')) {
                        const [hr, min] = value.split(':')
                        value = `${hr.padStart(3, '0')}:${min.padEnd(2, '0')}`
                    } else value = `${value.padStart(3, '0')}:00`
                }

                const clone =
                    tipos?.map((v, i) =>
                        i === id ? { ...v, [field]: value } : v,
                    ) ?? []

                const porcentagensClone = clone.filter(
                    it => it.tipo !== FALTAS_KEY,
                )

                const horasTotais = porcentagensClone.reduce(
                    (acc, curr) => acc + min(curr.quantidade),
                    0,
                )

                const porcentagens = porcentagensClone.reduce(
                    (acc, curr) =>
                        curr.tipo === FALTAS_KEY
                            ? acc
                            : {
                                  ...acc,
                                  [curr.tipo]: formatarHoras(
                                      curr.quantidade,
                                      3,
                                  ),
                              },
                    {},
                )

                setValue('porcentagens', porcentagens)
                setValue('horas', formatarHoras(horasTotais, 3))
                setValue(
                    'faltas',
                    clone.find(v => v.tipo === FALTAS_KEY)?.quantidade,
                )
                setTipos(clone)
            }}
            style={{ minHeight: 140 }}
        />,
    ]
}

function Loading() {
    return (
        <div className="flex flex-col flex-grow gap-4">
            <Skeleton className="h-[80px]" />
            <Skeleton className="h-[150px]" />
        </div>
    )
}
